import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarCheck, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Users, 
  Building2, 
  ArrowRight, 
  AlertCircle,
  FileText,
  Pencil,
  Scale,
  Calendar
} from 'lucide-react';
import { api } from '../../api/client';
import { getStatusBadge } from '../../utils/helpers';
import { showToast } from '../../components/common/Toast';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import Modal from '../../components/common/Modal';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state for confirmation
  const [selectedApt, setSelectedApt] = useState(null);
  const [modalType, setModalType] = useState('CONFIRM');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal state for edit
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [editFormData, setEditFormData] = useState({
    appointmentDate: '',
    startTime: '10:00',
    status: 'PENDING',
    adminNotes: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadData = () => {
    setLoading(true);
    api.get('/appointments/stats')
      .then((res) => {
        if (res?.success) {
          setStats(res.stats);
          setRecentAppointments(res.recentAppointments || []);
        }
      })
      .catch((err) => {
        if (err?.status !== 401) {
          showToast(err.message, 'error');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenAction = (apt, type) => {
    setSelectedApt(apt);
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleModalConfirm = async (appointmentId, note) => {
    setActionLoading(true);
    try {
      const endpoint = modalType === 'CONFIRM'
        ? `/appointments/${appointmentId}/confirm`
        : `/appointments/${appointmentId}/reject`;

      const res = await api.post(endpoint, { adminNote: note });
      if (res?.success) {
        showToast(res.message, 'success');
        setIsModalOpen(false);
        loadData();
      } else {
        showToast(res?.message || 'Xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEdit = (apt) => {
    setEditingApt(apt);
    setEditFormData({
      appointmentDate: apt.appointmentDate || '',
      startTime: apt.startTime || '10:00',
      status: apt.status || 'PENDING',
      adminNotes: apt.adminNote || '',
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingApt) return;
    setSavingEdit(true);
    try {
      const res = await api.put(`/appointments/${editingApt.id}`, editFormData);
      if (res?.success) {
        showToast('Uchrashuv muvaffaqiyatli yangilandi!', 'success');
        setIsEditModalOpen(false);
        setEditingApt(null);
        loadData();
      }
    } catch (err) {
      showToast(err.message || 'Tahrirlashda xatolik yuz berdi', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading && !stats) {
    return <div className="py-12 text-center text-slate-400">Dashboard yuklanmoqda...</div>;
  }

  return (
    <div className="space-y-8 animate-admin-fade-in">
      {/* 1. Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {/* Pending */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Kutilayotgan"
            >
              Kutilayotgan
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-amber-50 text-amber-700 border border-amber-200/60 shrink-0 flex items-center justify-center shadow-2xs">
              <Clock className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.pending ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Tasdiqlash talab etiladi"
            >
              Tasdiqlash talab etiladi
            </span>
          </div>
        </div>

        {/* Today */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Bugungi qabul"
            >
              Bugungi qabul
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-slate-100 text-slate-700 border border-slate-200/80 shrink-0 flex items-center justify-center shadow-2xs">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.today ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Bugungi uchrashuvlar"
            >
              Bugungi uchrashuvlar
            </span>
          </div>
        </div>

        {/* Confirmed */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Tasdiqlangan"
            >
              Tasdiqlangan
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200/60 shrink-0 flex items-center justify-center shadow-2xs">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.confirmed ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Grafikka kiritilgan"
            >
              Grafikka kiritilgan
            </span>
          </div>
        </div>

        {/* Completed */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Bajarilgan"
            >
              Bajarilgan
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-blue-50 text-blue-700 border border-blue-200/60 shrink-0 flex items-center justify-center shadow-2xs">
              <FileText className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.completed ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Muvaffaqiyatli qabul"
            >
              Muvaffaqiyatli qabul
            </span>
          </div>
        </div>

        {/* Lawyers */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Advokatlar"
            >
              Advokatlar
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-amber-50/80 text-[#966b1e] border border-amber-200/60 shrink-0 flex items-center justify-center shadow-2xs">
              <Scale className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.lawyers ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Faol mutaxassislar"
            >
              Faol mutaxassislar
            </span>
          </div>
        </div>

        {/* Clients */}
        <div className="relative overflow-hidden bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all duration-200 group flex flex-col justify-between min-h-[114px]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5 min-w-0">
            <span 
              className="text-[10px] xl:text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate min-w-0" 
              title="Mijozlar Bazasi"
            >
              Mijozlar Bazasi
            </span>
            <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-lg bg-slate-100 text-slate-700 border border-slate-200/80 shrink-0 flex items-center justify-center shadow-2xs">
              <Users className="w-3.5 h-3.5 shrink-0" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-none">
              {stats?.clients ?? 0}
            </div>
            <span 
              className="text-[10px] sm:text-[11px] text-slate-400 mt-1.5 block truncate leading-tight" 
              title="Ro‘yxatdagi mijozlar"
            >
              Ro‘yxatdagi mijozlar
            </span>
          </div>
        </div>
      </div>

      {/* 2. Pending & Recent Appointments Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-base font-bold text-slate-900">
              So‘nggi Arizalar va Uchrashuvlar
            </h2>
            <p className="text-xs text-slate-500">
              Yangi kelib tushgan qabul so‘rovlarini tasdiqlash yoki boshqarish
            </p>
          </div>

          <Link
            to="/admin/appointments"
            className="inline-flex items-center text-xs font-semibold text-[#b8935a] hover:text-[#091322] transition-colors"
          >
            Barcha arizalarni ko‘rish
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Bron ID</th>
                <th className="py-3 px-4">Mijoz</th>
                <th className="py-3 px-4">Advokat</th>
                <th className="py-3 px-4">Xizmat</th>
                <th className="py-3 px-4">Sana & Vaqt</th>
                <th className="py-3 px-4">Holat</th>
                <th className="py-3 px-4 text-right">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentAppointments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    Arizalar mavjud emas
                  </td>
                </tr>
              ) : (
                recentAppointments.map((apt) => {
                  const badge = getStatusBadge(apt.status);
                  return (
                    <tr key={apt.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-slate-800">
                        {apt.bookingReference}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{apt.clientName}</div>
                        <div className="text-[11px] text-slate-500">{apt.clientPhone}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {apt.lawyer?.fullName}
                      </td>
                      <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                        {apt.service?.titleUz}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-800">
                        {apt.appointmentDate} <span className="text-slate-400 font-sans">{apt.startTime}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.classes}`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${badge.dot}`} />
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center space-x-1.5 justify-end">
                          {apt.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => handleOpenAction(apt, 'CONFIRM')}
                                className="px-2.5 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors cursor-pointer"
                                title="Tasdiqlash (Ruxsat so‘rash va SMS yuborish)"
                              >
                                Tasdiqlash
                              </button>
                              <button
                                onClick={() => handleOpenAction(apt, 'REJECT')}
                                className="px-2.5 py-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded transition-colors cursor-pointer"
                                title="Rad etish"
                              >
                                Rad etish
                              </button>
                            </>
                          )}
                          {/* Colorless Edit Icon Button (User Requirement: rangsiz qlb ikonka ber) */}
                          <button
                            onClick={() => handleOpenEdit(apt)}
                            className="p-1.5 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-colors inline-flex items-center cursor-pointer"
                            title="Uchrashuvni tahrirlash"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        appointment={selectedApt}
        type={modalType}
        onConfirm={handleModalConfirm}
        loading={actionLoading}
      />

      {/* Edit Appointment Modal */}
      {editingApt && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingApt(null);
          }}
          title={`Uchrashuvni Tahrirlash: ${editingApt.bookingReference}`}
          maxWidth="max-w-lg"
        >
          <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-slate-700 space-y-1">
              <div><span className="font-semibold">Mijoz:</span> {editingApt.clientName} ({editingApt.clientPhone})</div>
              <div><span className="font-semibold">Advokat:</span> {editingApt.lawyer?.fullName}</div>
              <div><span className="font-semibold">Xizmat:</span> {editingApt.service?.titleUz}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Sana *</label>
                <input
                  type="date"
                  required
                  value={editFormData.appointmentDate}
                  onChange={(e) => setEditFormData({ ...editFormData, appointmentDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Vaqt *</label>
                <input
                  type="time"
                  required
                  value={editFormData.startTime}
                  onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Holat *</label>
              <select
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
              >
                <option value="PENDING">Kutilmoqda (PENDING)</option>
                <option value="CONFIRMED">Tasdiqlangan (CONFIRMED)</option>
                <option value="COMPLETED">Bajarilgan (COMPLETED)</option>
                <option value="CANCELLED">Bekor qilingan (CANCELLED)</option>
                <option value="REJECTED">Rad etilgan (REJECTED)</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Eslatma: Agar ariza adashib tasdiqlangan yoki bekor qilingan bo‘lsa, istalgan payt holatni qayta tiklashingiz yoki o‘zgartirishingiz mumkin.
              </p>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Ichki eslatma / Admin qaydlari</label>
              <textarea
                rows={3}
                value={editFormData.adminNotes}
                onChange={(e) => setEditFormData({ ...editFormData, adminNotes: e.target.value })}
                placeholder="Uchrashuv bo‘yicha eslatma yoki qo‘shimcha ma’lumot..."
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
              />
            </div>

            <div className="pt-3 border-t flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingApt(null);
                }}
                className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className="px-5 py-2 bg-[#091322] text-[#c5a880] hover:bg-[#12233b] font-semibold rounded cursor-pointer disabled:opacity-50"
              >
                {savingEdit ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
